import React, { useMemo } from 'react';
import { Select } from 'antd';
import { trpc } from '../../lib/api';

interface BuildingLocationSelectProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
}

type LocationRow = {
  id: string;
  name: string;
  categoryId?: string | null;
  mapMode?: 'image' | 'building' | 'folder' | 'geo' | null;
};

type LocationCategoryRow = {
  id: string;
  categoryLabel: string;
  isBuilding?: boolean;
  isActive?: boolean;
};

export const BuildingLocationSelect: React.FC<BuildingLocationSelectProps> = ({
  value,
  onChange,
  placeholder = '建物タイプのエリアを選択',
  allowClear = true,
  disabled = false,
}) => {
  const { data: locations = [], isLoading: locationsLoading } = trpc.location.getList.useQuery({});
  const { data: categories = [], isLoading: categoriesLoading } = trpc.locationCategory.list.useQuery({
    includeInactive: false,
  });

  const isLoading = locationsLoading || categoriesLoading;

  const buildingCategoryIds = useMemo(() => {
    return (categories as LocationCategoryRow[])
      .filter((category) => category.isBuilding)
      .map((category) => category.id);
  }, [categories]);

  const categoryLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    (categories as LocationCategoryRow[]).forEach((category) => {
      map.set(category.id, category.categoryLabel);
    });
    return map;
  }, [categories]);

  const options = useMemo(() => {
    const rows = locations as LocationRow[];
    return rows
      .filter((location) =>
        location.mapMode === 'building'
        || (Boolean(location.categoryId) && buildingCategoryIds.includes(location.categoryId!))
      )
      .sort((a, b) => a.name.localeCompare(b.name, 'ja'))
      .map((location) => {
        const categoryLabel = location.categoryId ? categoryLabelMap.get(location.categoryId) : null;
        const label = categoryLabel ? `${location.name} (${categoryLabel})` : location.name;
        return { label, value: location.id };
      });
  }, [locations, buildingCategoryIds, categoryLabelMap]);

  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      allowClear={allowClear}
      disabled={disabled || isLoading}
      loading={isLoading}
      options={options}
      showSearch
      filterOption={(input, option) =>
        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
      }
      notFoundContent={isLoading ? '読み込み中...' : '建物カテゴリのエリアがありません'}
      style={{ width: '100%' }}
    />
  );
};

export default BuildingLocationSelect;
